import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useAnimation, PanInfo } from "motion/react";
import "./Carousel.scss";

interface CarouselProps {
  children: React.ReactNode;
  gap?: number;
}

export const Carousel: React.FC<CarouselProps> = ({ children, gap = 12 }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const controls = useAnimation();
  const [maxDrag, setMaxDrag] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemWidth, setItemWidth] = useState(400);

  useEffect(() => {
    const updateConstraints = () => {
      if (wrapperRef.current && trackRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        const trackWidth = trackRef.current.scrollWidth;
        setMaxDrag(trackWidth - wrapperWidth);

        // Detect first child width
        const firstChild = trackRef.current.firstElementChild as HTMLElement;
        if (firstChild) {
          setItemWidth(firstChild.offsetWidth);
        }
      }
    };

    const timer = setTimeout(updateConstraints, 100);
    window.addEventListener("resize", updateConstraints);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateConstraints);
    };
  }, [children]);

  useEffect(() => {
    const itemStep = itemWidth + gap;
    const targetX = Math.max(-maxDrag, -currentIndex * itemStep);

    controls.start({
      x: targetX,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25,
        mass: 0.8,
      },
    });
  }, [currentIndex, itemWidth, gap, maxDrag, controls]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    const velocity = info.velocity.x;
    const offset = info.offset.x;
    const itemStep = itemWidth + gap;

    // Determine direction: -1 = next, +1 = prev
    let direction = 0;
    if (Math.abs(velocity) > 100 || Math.abs(offset) > 50) {
      direction = velocity > 0 || offset > 0 ? -1 : 1;
    }

    // Only move one card at a time
    let targetIndex = currentIndex + direction;

    // Clamp to valid range
    const maxIndex = Math.ceil(maxDrag / itemStep);
    targetIndex = Math.max(0, Math.min(targetIndex, maxIndex));

    setCurrentIndex(targetIndex);
  };

  return (
    <div className="carousel-wrapper" ref={wrapperRef}>
      <motion.div
        ref={trackRef}
        className="carousel-track"
        drag="x"
        dragConstraints={{ left: -maxDrag, right: 0 }}
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        animate={controls}
        style={{ x, gap }}
      >
        {children}
      </motion.div>
    </div>
  );
};
